<template>
    <section class="flex flex-col gap-4 px-5 pt-4 pb-5">
        <div class="flex items-center gap-2 flex-wrap">
            <div v-if="showRaters" class="flex items-center gap-2" role="group" :aria-label="t('reflectionsAdmin.panel.groupLabel')">
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="rounded-full scaffold-body-small"
                    :class="{ 'bg-toggle-option-selected': tab === 'participants' }"
                    :aria-pressed="tab === 'participants'"
                    @click="switchTab('participants')">
                    {{ t("reflectionsAdmin.panel.participantsTab", { count: participantCount }) }}
                </BaseButton>
                <BaseButton
                    theme="neutral"
                    size="sm"
                    class="rounded-full scaffold-body-small"
                    :class="{ 'bg-toggle-option-selected': tab === 'raters' }"
                    :aria-pressed="tab === 'raters'"
                    @click="switchTab('raters')">
                    {{ t("reflectionsAdmin.panel.ratersTab", { count: ratersInvited }) }}
                </BaseButton>
            </div>
            <h3 v-else class="body-semibold text-ink">{{ t("reflectionsAdmin.panel.participantsHeading", { count: participantCount }) }}</h3>
            <div class="ml-auto flex items-center gap-2 flex-wrap">
                <BaseButton
                    v-for="option in filterOptions"
                    :key="option.value"
                    theme="neutral"
                    size="sm"
                    class="rounded-full scaffold-body-small"
                    :class="{ 'bg-toggle-option-selected': filter === option.value }"
                    :aria-pressed="filter === option.value"
                    @click="selectFilter(option.value)">
                    {{ option.label }}
                </BaseButton>
                <div class="w-full md:w-auto md:min-w-80 md:max-w-md">
                    <BaseInput v-model="query" type="search" :placeholder="searchLabel" :aria-label="searchLabel">
                        <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                    </BaseInput>
                </div>
            </div>
        </div>

        <p v-if="loadError" class="body-small text-status-error" role="alert">{{ loadError }}</p>
        <p v-else-if="loading && !page" class="body-small text-ink-muted">{{ t("reflectionsAdmin.panel.loading") }}</p>
        <p v-else-if="page && page.total === 0" class="body-small text-ink-muted">{{ emptyText }}</p>

        <template v-else-if="page">
            <AdminParticipantTable
                v-if="tab === 'participants'"
                :rows="(page as AdminParticipantsPage).rows"
                :hide-rater-status="hideRaterStatus"
                :show-raters="!isSelfAssessmentOnly"
                :class="{ 'opacity-60': loading }" />
            <AdminRaterTable v-else :rows="(page as AdminRatersPage).rows" :campaign-closed="campaignState === 'closed'" :class="{ 'opacity-60': loading }" />

            <div class="flex items-center gap-3 flex-wrap px-2 details text-ink-muted">
                <span>{{ footerText }}</span>
                <div class="ml-auto inline-flex items-center gap-1">
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        icon-only
                        :aria-label="t('reflectionsAdmin.panel.previousPage')"
                        :disabled="page.page <= 1 || loading"
                        @click="goToPage(page.page - 1)">
                        <i class="bi bi-chevron-left" aria-hidden="true"></i>
                    </BaseButton>
                    <span class="min-w-18 text-center">{{ t("reflectionsAdmin.panel.pageOf", { page: page.page, pages: page.page_count }) }}</span>
                    <BaseButton
                        theme="neutral"
                        size="sm"
                        icon-only
                        :aria-label="t('reflectionsAdmin.panel.nextPage')"
                        :disabled="page.page >= page.page_count || loading"
                        @click="goToPage(page.page + 1)">
                        <i class="bi bi-chevron-right" aria-hidden="true"></i>
                    </BaseButton>
                </div>
            </div>
        </template>
    </section>
</template>

<script setup lang="ts">
import { refDebounced } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { AdminParticipantFilter, AdminParticipantsPage, AdminRaterFilter, AdminRatersPage, ReflectionCampaignState } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import AdminParticipantTable from "~vue/pages/reflections/AdminParticipantTable.vue";
import AdminRaterTable from "~vue/pages/reflections/AdminRaterTable.vue";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

type PanelTab = "participants" | "raters";
type PanelFilter = AdminParticipantFilter | AdminRaterFilter;

interface Props {
    campaignId: string;
    campaignState: ReflectionCampaignState;
    participantCount: number;
    ratersInvited: number;
    hideRaterStatus?: boolean;
    isSelfAssessmentOnly?: boolean;
}

const SEARCH_DEBOUNCE_MS = 300;

const { campaignId, campaignState, participantCount, ratersInvited, hideRaterStatus = false, isSelfAssessmentOnly = false } = defineProps<Props>();
const { t } = useI18n();

const tab = ref<PanelTab>("participants");
const filter = ref<PanelFilter>("all");
const query = ref("");
const debouncedQuery = refDebounced(query, SEARCH_DEBOUNCE_MS);
// What requests actually use. Typing reaches it through the debounce; a tab
// switch clears it at once so the new tab's first fetch never carries the old term.
const effectiveQuery = ref("");
const pageNumber = ref(1);
const page = ref<AdminParticipantsPage | AdminRatersPage | null>(null);
const loading = ref(false);
const loadError = ref<string | null>(null);

const showRaters = computed(() => !hideRaterStatus && !isSelfAssessmentOnly);
const onParticipants = computed(() => tab.value === "participants");

const filterOptions = computed<{ value: PanelFilter; label: string }[]>(() => {
    if (onParticipants.value) {
        return [
            { value: "all", label: t("reflectionsAdmin.panel.filter.all") },
            { value: "not_started", label: t("reflectionsAdmin.panel.filter.notStarted") },
            { value: "in_progress", label: t("reflectionsAdmin.panel.filter.inProgress") },
            { value: "submitted", label: t("reflectionsAdmin.panel.filter.submitted") },
            { value: "report_unviewed", label: t("reflectionsAdmin.panel.filter.reportNotViewed") },
        ];
    }
    return [
        { value: "all", label: t("reflectionsAdmin.panel.filter.all") },
        // Pending means "hasn't responded"; once the campaign closes nobody still can.
        { value: "pending", label: campaignState === "closed" ? t("reflectionsAdmin.panel.filter.noResponse") : t("reflectionsAdmin.panel.filter.pending") },
        { value: "responded", label: t("reflectionsAdmin.panel.filter.responded") },
    ];
});

const searchLabel = computed(() => (onParticipants.value ? t("reflectionsAdmin.panel.searchParticipants") : t("reflectionsAdmin.panel.searchRaters")));

const emptyText = computed(() => {
    const filtered = effectiveQuery.value !== "" || filter.value !== "all";
    if (onParticipants.value) {
        return filtered ? t("reflectionsAdmin.panel.noParticipantsMatch") : t("reflectionsAdmin.panel.noParticipantsYet");
    }
    return filtered ? t("reflectionsAdmin.panel.noRatersMatch") : t("reflectionsAdmin.panel.noRatersYet");
});

const footerText = computed(() => {
    if (!page.value) {
        return "";
    }
    const from = page.value.total ? (page.value.page - 1) * page.value.page_size + 1 : 0;
    const to = Math.min(page.value.total, page.value.page * page.value.page_size);
    const counts = { from, to, total: page.value.total };
    return onParticipants.value ? t("reflectionsAdmin.panel.showingParticipants", counts) : t("reflectionsAdmin.panel.showingRaters", counts);
});

let requestSequence = 0;

async function load() {
    const sequence = ++requestSequence;
    loading.value = true;
    loadError.value = null;
    const params = new URLSearchParams({ state: filter.value, q: effectiveQuery.value, page: String(pageNumber.value) });
    const { data, error: fetchError } = await useFetch(`/v1/reflections/admin/campaigns/${campaignId}/${tab.value}?${params}`).json<AdminParticipantsPage | AdminRatersPage>();
    if (sequence !== requestSequence) {
        return;
    }
    loading.value = false;
    if (fetchError.value) {
        const fallback = onParticipants.value ? t("reflectionsAdmin.panel.loadParticipantsFailed") : t("reflectionsAdmin.panel.loadRatersFailed");
        loadError.value = fetchError.value.message || fallback;
        return;
    }
    page.value = data.value ?? null;
}

function switchTab(next: PanelTab) {
    if (tab.value === next) {
        return;
    }
    tab.value = next;
    filter.value = "all";
    query.value = "";
    effectiveQuery.value = "";
    pageNumber.value = 1;
    page.value = null;
}

function selectFilter(next: PanelFilter) {
    filter.value = next;
    pageNumber.value = 1;
}

function goToPage(next: number) {
    pageNumber.value = next;
}

// A new search term always starts from page one.
watch(debouncedQuery, (value) => {
    if (effectiveQuery.value !== value) {
        effectiveQuery.value = value;
        pageNumber.value = 1;
    }
});

watch([tab, filter, effectiveQuery, pageNumber], () => {
    void load();
});

onMounted(() => {
    void load();
});

onBeforeUnmount(() => {
    requestSequence += 1;
});
</script>
